import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { SharedService } from './../../../../services/shared/shared.service';

@Component({
  selector: 'custom-advanced-filters',
  templateUrl: './custom-advanced-filters.component.html',
  styleUrls: ['./custom-advanced-filters.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class CustomAdvancedFiltersComponent implements OnChanges {
  @Input() validators: Validators[];
  @Input() groupValidators: Validators[];
  @Input() validationMessage: string;
  @Input() tooltip: string;
  @Input() filter: any;
  @Input() embeddedComponent = false;
  @Output() filterSelectedEE = new EventEmitter<any>();

  customRangeForm: UntypedFormGroup;
  show: boolean;

  constructor(
    private _fb: UntypedFormBuilder,
    private _sharedService: SharedService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.customRangeForm) {
      this.customRangeForm = this._fb.group(
        {
          from: ['', this.validators],
          to: ['', this.validators],
        },
        { validators: this.groupValidators },
      );
    }
    if (changes.filter?.currentValue) {
      changes.filter?.currentValue.rangeFields.forEach((r) => {
        this.customRangeForm.patchValue({ from: r.from, to: r.to });
      });

      if (changes.filter?.currentValue?.dropdownFields.some((d) => d.selected)) {
        this.show = true;
      }
    }
  }

  selectFilter(event) {
    this.filter.dropdownFields.find((x) => x.value == event.target.value).selected =
      event.target.checked;

    const selectedValues = this.filter?.dropdownFields
      ?.filter((x) => x.selected)
      .map((x) => x['value'])
      .concat(
        this.filter?.rangeFields
          .filter((r) => SharedService.isNotEmpty(r.from))
          .map((r) => `${r.from}<=>${r.to}`),
      );

    this.filterSelectedEE.emit({ value: selectedValues, filter: this.filter.filterIndex });
  }

  toggleFilter() {
    this.show = !this.show;
  }

  /**
   * Validates the form and either emits the changed values or displays an error message.
   * @param data
   */
  validForm(data: UntypedFormGroup) {
    let from = data.value.from.trim();
    let to = data.value.to.trim();

    // If range has blank, value of the field will be set.
    if (from === '') {
      from = to;
    } else if (to === '') {
      to = from;
    }

    // Catch condition that cannot be validated
    if (from === '*' && to === '*') {
      this._sharedService.openSnackBar('A value is required', 'ok');
      return;
    }

    if (data.invalid) {
      let message = 'Invalid form details';
      if (data.errors) {
        message = this.getFormErrors(data).join(', ');
      } else if (data.get('from').invalid) {
        message = this.getFormErrors(data.get('from')).join(', ');
      } else if (data.get('to').invalid) {
        message = this.getFormErrors(data.get('to')).join(', ');
      }

      this._sharedService.openSnackBar(message, 'ok');
    } else {
      const selectedValues = this.filter?.dropdownFields
        ?.filter((x) => x.selected)
        .map((x) => x['value']);

      selectedValues.push(`${from}<=>${to}`);
      this.filterSelectedEE.emit({ value: selectedValues, filter: this.filter.filterIndex });
    }
  }

  /**
   * Gets the corrisponding error messages from all errors on a formControl
   * @param formControl
   * @returns Validation error messages
   */
  getFormErrors(formControl: AbstractControl): string[] {
    const keys: string[] = Object.keys(formControl.errors);
    const errors: string[] = [];
    keys.forEach((key) => {
      errors.push(this.validationMessage[key]);
    });

    return errors;
  }

  collapse(): void {
    this.show = false;
  }
}
